import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Divider,
  Paper,
  Grid,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import MDButton from "shared/components/MDButton";
import logoImage from "../../../../../assets/images/logo horinzontal.png";

const SuccessDialog = ({
  open,
  onClose,
  data,
  autoClose = true,
  autoCloseDelay = 3000,
  title = "¡Registro Creado Exitosamente!",
  subtitle = "El registro ha sido creado en el sistema",
  entityType = "propiedad", // 'usuario' o 'propiedad'
}) => {
  const [countdown, setCountdown] = useState(autoCloseDelay / 1000);

  useEffect(() => {
    if (open && autoClose) {
      setCountdown(autoCloseDelay / 1000);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            onClose();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [open, autoClose, autoCloseDelay, onClose]);

  if (!data) return null;

  // Renderizar datos según el tipo de entidad
  const renderData = () => {
    if (entityType === "propiedad") {
      return (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1}>
              <BusinessIcon sx={{ color: "#09702d" }} />
              <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                DATOS DE LA PROPIEDAD
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Nombre
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {data.name || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              IP
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {data.ip || "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Descripción
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {data.description || "Sin descripción"}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              ID Copropietario
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {data.idcopropietarie || "N/A"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Estado
            </Typography>
            <Typography
              variant="body2"
              fontWeight="bold"
              color={data.status === 1 ? "success.main" : "error.main"}
            >
              {data.status === 1 ? "ACTIVO" : "INACTIVO"}
            </Typography>
          </Grid>
        </Grid>
      );
    }

    // Para usuarios (mantener el formato existente)
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon sx={{ color: "#09702d" }} />
            <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
              DATOS DEL USUARIO
            </Typography>
          </Box>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Placa
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {data.plate || "N/A"}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Tipo
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {data.idvehicletype === 1 ? "Vehículo" : "Moto"}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Nombre
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {data.name || "N/A"} {data.lastname || ""}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Cédula
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {data.cedula || "N/A"}
          </Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Residencial
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {data.residential || "N/A"}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">
            Creado Por
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {data.UserGoper || "N/A"}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
          overflow: "hidden",
        },
      }}
    >
      {/* Header con gradiente verde */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #09702d 0%, #0a8511 100%)",
          p: 3,
          pb: 4,
          textAlign: "center",
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { color: "white" },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Logo */}
        <Box
          component="img"
          src={logoImage}
          alt="GoPass"
          sx={{
            height: 50,
            width: "auto",
            objectFit: "contain",
            mb: 2,
            filter: "brightness(0) invert(1)",
          }}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />

        {/* Icono de éxito */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: "rgba(255,255,255,0.2)",
              borderRadius: "50%",
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 64, color: "white" }} />
          </Box>
        </Box>

        <Typography variant="h5" fontWeight="bold" color="white" sx={{ mt: 2 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="rgba(255,255,255,0.85)">
          {subtitle}
        </Typography>
      </Box>

      {/* Contenido */}
      <DialogContent sx={{ pt: 3, px: 3, pb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: "12px",
            border: "1px solid #e8f5e9",
            backgroundColor: "#f1f8e9",
          }}
        >
          {renderData()}
        </Paper>

        {/* Contador de cierre automático */}
        {autoClose && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              Cerrando en {countdown} segundos...
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <MDButton
            variant="contained"
            color="primary"
            onClick={onClose}
            startIcon={<CheckCircleIcon />}
            sx={{
              borderRadius: "8px",
              fontWeight: "bold",
              px: 4,
              height: "48px",
            }}
          >
            Aceptar
          </MDButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

SuccessDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  autoClose: PropTypes.bool,
  autoCloseDelay: PropTypes.number,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  entityType: PropTypes.oneOf(["usuario", "propiedad"]),
};

SuccessDialog.defaultProps = {
  data: null,
  autoClose: true,
  autoCloseDelay: 3000,
  title: "¡Registro Creado Exitosamente!",
  subtitle: "El registro ha sido creado en el sistema",
  entityType: "propiedad",
};

export default SuccessDialog;
